import axios from "axios";
import { API_URL, BUCKET_URL } from "../config/config";
import { getHeaderOptions } from "./getHeaderOptions";
import logger from "../utils/logger";

const API_ENDPOINT = API_URL + "api/v1";

export const getAllContent = (params: any) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  return axios.get(`${API_ENDPOINT}/content`, { headers: obj, params: params });
};

export const getContentById = (id: string) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  return axios.get(`${API_ENDPOINT}/content/${id}`, { headers: obj });
};

export const deleteContent = (id: string) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  return axios.delete(`${API_ENDPOINT}/content/${id}`, { headers: obj });
};

export const createNewContent = (data: any) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  return axios.post(`${API_ENDPOINT}/content`, data, { headers: obj });
};

export const updateContent = (data: any, id: string) => {
  let obj = {
    ...getHeaderOptions,
    Authorization: localStorage.getItem("auth_token") || "",
  };
  return axios.put(`${API_ENDPOINT}/content/${id}`, data, { headers: obj });
};

export const getContentByType = (type: string, options?: { search?: string, page?: number, perPage?: number }) => {
  const params: any = { type };

  if (options) {
    if (options.search) params.search = options.search;
    if (options.page) params.page = options.page;
    if (options.perPage) params.perPage = options.perPage;
  }

  return getAllContent(params);
};



/**
 * Download content file using the exact filePath from API - NO MODIFICATIONS
 * @param filePath - Complete S3 path like "content/68526f3216a3da763228b32d/1751546176694.jpg" 
 * @param fileName - Display name for the downloaded file (NOT used in URL construction)
 */
export const downloadContentFile = async (
  filePath: string, 
  fileName: string
): Promise<Blob> => {
  try {
    logger.log(`📥 Downloading: ${fileName}`);
    logger.log(`🛤️ File path received: ${filePath}`);
    
    // 🔥 CRITICAL: Validate filePath format but DON'T modify it
    if (!filePath || !filePath.startsWith('content/')) {
      throw new Error(`Invalid filePath format: "${filePath}". Expected: content/contentId/timestamp.ext`);
    }
    
    // 🔥 CRITICAL: Use filePath AS-IS, no modifications
    const fileUrl = `${BUCKET_URL}${filePath}`;
    logger.log(`🔗 Final URL: ${fileUrl}`);
    
    const response = await axios.get(fileUrl, {
      responseType: 'blob',
      timeout: 15000
    });
    
    if (response.data && response.data.size > 0) {
      logger.log(`✅ Downloaded: ${fileName} (${response.data.size} bytes)`);
      return response.data;
    }
    
    throw new Error('No data received or empty file');
    
  } catch (error: any) {
    logger.error(`❌ Download failed for ${fileName}:`, {
      status: error.response?.status,
      statusText: error.response?.statusText,
      receivedFilePath: filePath,
      finalUrl: `${BUCKET_URL}${filePath}`,
      error: error.message
    });
    
    throw new Error(`Failed to download ${fileName}: ${error.response?.status === 404 ? 'File not found in S3' : error.message}`);
  }
};
/**
 * Convert downloaded blob to File object for upload system
 * @param blob - The downloaded blob
 * @param fileName - Original file name
 * @returns File object ready for upload
 */
export const contentBlobToFile = (blob: Blob, fileName: string): File => {
  // Detect MIME type from file extension for proper file handling
  const extension = fileName.split('.').pop()?.toLowerCase();
  let mimeType = 'application/octet-stream'; // Default fallback
  
  // Map common file extensions to MIME types
  const mimeMap: {[key: string]: string} = {
    'pdf': 'application/pdf',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg', 
    'png': 'image/png',
    'mp4': 'video/mp4',
    '3gp': 'video/3gpp',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'txt': 'text/plain'
  };
  
  if (extension && mimeMap[extension]) {
    mimeType = mimeMap[extension];
  }
  
  // Create File object with correct MIME type
  const file = new File([blob], fileName, { type: mimeType });
  
  logger.log(`📄 Created File object: ${fileName} (${file.size} bytes, ${mimeType})`);
  
  return file;
};